import { inject, Injectable, signal } from "@angular/core";
import { SwPush } from "@angular/service-worker";
import { WebPushApiService } from "./web-push-api.service";
import { WEB_PUSH_VAPID_PUBLIC_KEY } from "../injection-tokens";
import { injectUiInteractionTracker } from "@pcp/insights";

@Injectable({
    providedIn: 'root'
})
export class WebPushHelperService {
    private readonly swPush = inject(SwPush);
    private readonly webPushApiService = inject(WebPushApiService);
    private readonly vapidPublicKey = inject(WEB_PUSH_VAPID_PUBLIC_KEY);

    /** `UiInteraction` is ETL-locked; the tracker fixes the name and logger scope. */
    private readonly trackUiInteraction = injectUiInteractionTracker();

    readonly permissionState = signal<'granted'|'denied'|'prompt'|undefined>(undefined);

    constructor() {
        if ('Notification' in window) {
            navigator.permissions.query({ name: "notifications" })
                .then((permissionStatus) => {
                    this.permissionState.set(permissionStatus.state);

                    permissionStatus.addEventListener("change", () => {
                        this.permissionState.set(permissionStatus.state);
                    });
                });
        }
    }

    /**
     * 1. request permissions to receive push notifications
     * 2. create a subscription and send it to the server
     */
    async requestAndSaveSubscription() {
        // NOTE: angular would actually also request the permission, but we cannot evaluate the result.
        //       -> request permissions manually in order to log the result
        const permission = await Notification.requestPermission();
        this.trackUiInteraction('WebPush', 'RequestPermissions', '', 'grantedResult', permission);

        if (permission !== 'granted') {
            return;
        }

        return await this.createAndSaveSubscription();
    }

    /**
     * create and save a push subscription without asking for permissions.
     */
    async createAndSaveSubscription() {
        const subscription = await this.swPush.requestSubscription({
            serverPublicKey: this.vapidPublicKey,
        });

        this.webPushApiService.sendSubscription(subscription);
    }

    /**
     * detects if the WebPush feature is enabled in the browser.
     */
    pushManagerEnabled() {
        return this.swPush.isEnabled && 'Notification' in window;
    }
}
